import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useReportScope } from '../../lib/useReportScope';
import MultiSelectFilter from '../../components/workspace/MultiSelectFilter';
import ReportPdfButton, { ExcelIcon, PdfIcon, excelBtnStyle, downloadExcel } from '../../components/workspace/ReportPdfButton';

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

function fmtINR(n) {
  if (n == null) return '—';
  return '₹' + Number(n).toLocaleString('en-IN');
}

// jsPDF's built-in fonts (helvetica/times/courier) only support WinAnsi
// encoding, which has no glyph for ₹ (U+20B9) — it renders as a stray
// apostrophe/box in the PDF. Use an ASCII-safe "Rs." prefix for PDF export.
function fmtINRPdf(n) {
  if (n == null) return '—';
  return 'Rs. ' + Number(n).toLocaleString('en-IN');
}

// Converts a "YYYY-MM" month-input value to the first/last calendar day of that month.
// Built from local Date getters (not toISOString(), which converts to UTC and
// can shift the date back a day in timezones ahead of UTC, e.g. IST).
function monthStart(m) { return m ? `${m}-01` : ''; }
function monthEnd(m) {
  if (!m) return '';
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo, 0); // day 0 of month `mo` (1-indexed) = last day of that month, local time
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px',
      textAlign: right ? 'right' : 'left',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      whiteSpace: 'nowrap',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

export default function FinancialReport() {
  const { appId } = useParams();
  const api = useApi();
  const { isAdmin, scopeLoading } = useReportScope(appId);

  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]); // empty = all
  const [from, setFrom] = useState(currentMonth);
  const [to, setTo]     = useState(currentMonth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.listProjectsSimple(appId)
      .then(r => setProjects(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {});
  }, [appId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (projectFilter.length > 0) params.project_ids = projectFilter.join(',');
      if (from) params.from = monthStart(from);
      if (to)   params.to   = monthEnd(to);
      const data = await api.getProjectFinancialsSummary(appId, params);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load financial report');
    } finally {
      setLoading(false);
    }
  }, [appId, projectFilter, from, to]);

  useEffect(() => { if (isAdmin) load(); }, [load, isAdmin]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    total_billing:     t.total_billing     + (r.total_billing || 0),
    total_actual_cost: t.total_actual_cost + (r.total_actual_cost || 0),
    invoiced_total:    t.invoiced_total    + (r.invoiced_total || 0),
    profit:            t.profit            + (r.profit || 0),
  }), { total_billing: 0, total_actual_cost: 0, invoiced_total: 0, profit: 0 }), [rows]);

  const excelTable = useMemo(() => ({
    headers: ['Project', 'Client', 'Start Date', 'End Date', 'Billing Type', 'Total Billing', 'Estimated Cost', 'Actual Labor Cost', 'Other Costs', 'Invoiced', 'Total Actual Cost', 'Profit', 'Margin %'],
    rows: rows.map(r => [
      r.name, r.client_name || '—',
      r.start_date ? formatDate(r.start_date) : '—', r.end_date ? formatDate(r.end_date) : '—',
      r.billing_type || '—',
      fmtINR(r.total_billing), fmtINR(r.estimated_cost), fmtINR(r.actual_labor_cost),
      fmtINR(r.other_costs_total), fmtINR(r.invoiced_total), fmtINR(r.total_actual_cost),
      fmtINR(r.profit), r.margin_pct != null ? `${r.margin_pct}%` : '—',
    ]),
  }), [rows]);

  // PDF export uses an ASCII-safe currency format — see fmtINRPdf above.
  const pdfTable = useMemo(() => ({
    headers: excelTable.headers,
    rows: rows.map(r => [
      r.name, r.client_name || '—',
      r.start_date ? formatDate(r.start_date) : '—', r.end_date ? formatDate(r.end_date) : '—',
      r.billing_type || '—',
      fmtINRPdf(r.total_billing), fmtINRPdf(r.estimated_cost), fmtINRPdf(r.actual_labor_cost),
      fmtINRPdf(r.other_costs_total), fmtINRPdf(r.invoiced_total), fmtINRPdf(r.total_actual_cost),
      fmtINRPdf(r.profit), r.margin_pct != null ? `${r.margin_pct}%` : '—',
    ]),
  }), [rows, excelTable.headers]);

  const periodLabel = from || to
    ? `Start Date: ${from || '…'}  •  End Date: ${to || '…'}`
    : 'Period: All time'; // from/to are "YYYY-MM" month values

  if (scopeLoading) {
    return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
  }
  if (!isAdmin) {
    return <Navigate to={`/apps/${appId}/home`} replace />;
  }

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Financial Report</h1>
          <p className="tl-subtitle">Billing, costs, invoicing, and profitability across projects.</p>
        </div>
        {!loading && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => downloadExcel('financial-report.xls', 'Financial Report', periodLabel, excelTable.headers, excelTable.rows)}
              disabled={rows.length === 0}
              style={{ ...excelBtnStyle, opacity: rows.length === 0 ? 0.4 : 1 }}
            >
              <ExcelIcon /> Excel
            </button>
            <ReportPdfButton
              title="Financial Report"
              subtitle={periodLabel}
              table={pdfTable}
              icon={<PdfIcon />}
            />
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap',
        marginBottom: 24, padding: '16px 20px',
        background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Project</label>
          <MultiSelectFilter
            options={projects.map(p => ({ value: String(p.id), label: p.name }))}
            selected={projectFilter}
            onChange={setProjectFilter}
            placeholder="All Projects"
            minWidth={220}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Start Date</label>
          <input
            type="month" value={from} onChange={e => setFrom(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>End Date</label>
          <input
            type="month" value={to} onChange={e => setTo(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
          />
        </div>
        {(from || to || projectFilter.length > 0) && (
          <button
            onClick={() => { setFrom(''); setTo(''); setProjectFilter([]); }}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}
          >Reset</button>
        )}
      </div>

      {/* Summary cards */}
      {!loading && rows.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Billing',     value: fmtINR(totals.total_billing),     color: 'var(--text-primary)' },
            { label: 'Total Actual Cost', value: fmtINR(totals.total_actual_cost), color: '#f87171' },
            { label: 'Total Invoiced',    value: fmtINR(totals.invoiced_total),    color: '#38bdf8' },
            { label: 'Total Profit',      value: fmtINR(totals.profit),            color: totals.profit >= 0 ? '#4ade80' : '#f87171' },
          ].map(c => (
            <div key={c.label} style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 150,
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No financial data found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>Project</Th>
                <Th>Client</Th>
                <Th>Start Date</Th>
                <Th>End Date</Th>
                <Th>Billing Type</Th>
                <Th right>Total Billing</Th>
                <Th right>Estimated Cost</Th>
                <Th right>Actual Labor Cost</Th>
                <Th right>Other Costs</Th>
                <Th right>Invoiced</Th>
                <Th right>Total Actual Cost</Th>
                <Th right>Profit</Th>
                <Th right>Margin %</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)', borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{r.client_name || '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.start_date ? formatDate(r.start_date) : '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.end_date ? formatDate(r.end_date) : '—'}</td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.billing_type || '—'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.total_billing)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.estimated_cost)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.actual_labor_cost)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.other_costs_total)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.invoiced_total)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtINR(r.total_actual_cost)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: (r.profit || 0) >= 0 ? '#4ade80' : '#f87171' }}>{fmtINR(r.profit)}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.margin_pct != null ? `${r.margin_pct}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
